import {Injectable} from '@angular/core';
import {ApiService, GetListResponse, ModelBase} from '@app/api/api.service';
import { SoftwareVersionsObject } from '@app/api/software-images-api.service';

export interface OpenLabSystem extends ModelBase {
    name: string;
    olss_password: string;
    olss_server: string;
    olss_connection_type: string;
    olss_username: string;
    olss_description: string;
    shared_download_location: string;
    use_anonymous_user: boolean,
    shared_download_username: string;
    shared_download_password: string;
    customer_id: number;
    is_locked: boolean;
    software_versions_json: SoftwareVersionsObject;
}

interface GetOpenLabSystemsResponse extends GetListResponse {
    openlab_systems: OpenLabSystem[];
}

interface GetOpenLabSystemsArguments {
    customerId: number;
    offset: number;
    limit: number;
    aicVersionName?: string;
    ols_ids?: number[];
}

interface GetAcCountResponse {
    openlab_system_id: number;
    ac_count: number;
}

@Injectable({
    providedIn: 'root'
})
export class OpenLabSystemApiService {

    constructor(
        private apiService: ApiService
    ) {}

    public async getOpenLabSystems(args: GetOpenLabSystemsArguments): Promise<GetOpenLabSystemsResponse> {
        const {
            customerId,
            offset,
            limit,
            aicVersionName,
            ols_ids
        } = args;
        return this.apiService.getResponseJson({
            urlPart: '/openlab-systems',
            queryStringParams: {
                customer_id: customerId,
                offset: offset,
                limit: limit,
                aic_version_name: aicVersionName,
                ols_ids: ols_ids
            }
        });
    }

    public async getOpenLabSystem(
        args: { systemId: number }
    ): Promise<{ openlab_system: OpenLabSystem }> {
        const {systemId} = args;
        return this.apiService
            .getResponseJson(
                {
                    urlPart: `/openlab-systems/${systemId}`
                });
    }

    public async getOpenLabSystemAcCount(args: {openlab_system: OpenLabSystem}): Promise<GetAcCountResponse> {
        const {openlab_system} = args;
        return this.apiService.getResponseJson({
            urlPart: `/openlab-system/${openlab_system.id}/ac-count`,
            method: 'GET'
        });
    }

    public async deleteOpenLabSystem(args: { openlab_system: OpenLabSystem, reason: string}): Promise<{ openlab_system: OpenLabSystem }> {
        const {openlab_system, reason} = args;
        return this.apiService.getResponseJson({
            urlPart: `/openlab-systems/${openlab_system.id}`,
            method: 'DELETE',
            data: {
                reason: reason
            }
        });
    }

    public async updateOpenLabSystem(args: { openlab_system: OpenLabSystem, reason?: string}): Promise<{ openlab_system: OpenLabSystem }> {
        const {openlab_system, reason} = args;
        return this.apiService.getResponseJson({
            urlPart: `/openlab-systems/${openlab_system.id}`,
            method: 'PUT',
            data: {
                openlab_system: openlab_system,
                reason: reason
            }
        });
    }

    public async createOpenLabSystem(args: { openlab_system: OpenLabSystem }): Promise<{ openlab_system: OpenLabSystem }> {
        const {openlab_system} = args;
        return this.apiService.getResponseJson({
            urlPart: `/openlab-systems/new`,
            method: 'POST',
            data: openlab_system
        });
    }

    public async lockOpenLabSystem(args: { openlab_system: OpenLabSystem, reason?: string }): Promise<{ openlab_system: OpenLabSystem }> {
        const {openlab_system, reason} = args;
        return this.apiService.getResponseJson({
            urlPart: `/openlab-systems/${openlab_system.id}/lock`,
            method: 'POST',
            data: {
                is_locked: openlab_system.is_locked,
                reason: reason
            }
        });
    }
}
